import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { ArrowRight, ChevronRight, FlaskConical, Scale, SlidersHorizontal } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { Reveal } from "@/components/Reveal";
import { bySlug, CALCULATORS, ICONS } from "@/data/calculators";
import { ARTICLES } from "@/data/articles";
import { Seo } from "@/components/Seo";
import { API } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { nf } from "@/lib/format";

function Field({ def, value, onChange }) {
  if (def.type === "date") {
    return (
      <div>
        <Label htmlFor={def.key} className="font-mono text-xs uppercase tracking-[0.2em] text-ink-muted">
          {def.label}
        </Label>
        <Input
          id={def.key}
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`calculator-input-${def.key}`}
          className="mt-2 h-11 border-input bg-card font-mono text-sm"
        />
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={def.key} className="font-mono text-xs uppercase tracking-[0.2em] text-ink-muted">
          {def.label}
        </Label>
        <span className="font-mono text-sm font-bold" data-testid={`calculator-value-${def.key}`}>
          {value} <span className="text-ink-muted">{def.unit}</span>
        </span>
      </div>
      <Slider
        id={def.key}
        value={[value]}
        min={def.min}
        max={def.max}
        step={def.step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={`calculator-input-${def.key}`}
        className="mt-3"
      />
    </div>
  );
}

function Chart({ chart, accent }) {
  return (
    <div className="rounded-md border border-border bg-secondary/30 p-5" data-testid="result-chart">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">{chart.title}</p>
      <div className="mt-4 h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart.data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fontFamily: "Space Mono", fill: "var(--ink-muted)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
              contentStyle={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontFamily: "Space Mono",
                fontSize: 12,
              }}
            />
            <Bar dataKey="value" radius={[3, 3, 0, 0]}>
              {chart.data.map((_, i) => (
                <Cell key={i} fill={accent} fillOpacity={0.35 + (0.65 * (i + 1)) / chart.data.length} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function CalculatorPage({ slug }) {
  const calc = bySlug(slug);
  const [values, setValues] = useState(() => (calc ? calc.defaults() : null));
  const [count, setCount] = useState(null);

  useEffect(() => {
    if (!calc) return;
    document.title = `${calc.title} — Curiosímetro`;
    let tracked = false;
    try {
      tracked = sessionStorage.getItem(`cm-tracked-${slug}`) === "1";
    } catch (e) {}
    if (!tracked) {
      try {
        sessionStorage.setItem(`cm-tracked-${slug}`, "1");
      } catch (e) {}
      fetch(`${API}/track/${slug}`, { method: "POST" })
        .then((r) => r.json())
        .then((d) => setCount(d.count))
        .catch(() => {});
    }
  }, [calc, slug]);

  const results = useMemo(() => (calc ? calc.compute(values) : null), [calc, values]);

  if (!calc || !values || !results) return <Navigate to="/" replace />;

  const Icon = ICONS[calc.icon];
  const others = CALCULATORS.filter((c) => c.slug !== slug);

  return (
    <>
      <Nav />
      <Seo
        title={`${calc.title} — Curiosímetro`}
        description={calc.description}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebApplication",
              name: `${calc.title} — Curiosímetro`,
              inLanguage: "es",
              description: calc.description,
              applicationCategory: "UtilitiesApplication",
              operatingSystem: "Web",
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "¿Cómo se calcula este dato?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: `${results.method.formula}. ${results.method.sources}`,
                  },
                },
              ],
            },
          ],
        }}
      />
      <main className="mx-auto max-w-7xl px-5 pb-20 sm:px-8" data-testid="calculator-page">
        <nav className="flex items-center gap-2 pt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted" data-testid="breadcrumbs">
          <Link to="/" className="transition-colors hover:text-primary">Inicio</Link>
          <ChevronRight className="h-3 w-3" />
          <span aria-current="page" style={{ color: calc.accent }}>{calc.title}</span>
        </nav>

        <Reveal className="mt-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-md" style={{ backgroundColor: `${calc.accent}14`, color: calc.accent }}>
              <Icon className="h-5 w-5" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-muted">Unidad: {calc.unit}</span>
          </div>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium tracking-tight sm:text-5xl">{calc.title}</h1>
          <p className="mt-4 max-w-2xl text-lg font-light leading-relaxed text-muted-foreground">{calc.description}</p>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="rounded-lg border border-border bg-card p-7 shadow-offset lg:sticky lg:top-24">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4" style={{ color: calc.accent }} />
                <h2 className="font-serif text-xl">Tus datos</h2>
              </div>
              <div className="mt-7 space-y-7">
                {calc.inputs.map((def) => (
                  <Field
                    key={def.key}
                    def={def}
                    value={values[def.key]}
                    onChange={(v) => setValues((prev) => ({ ...prev, [def.key]: v }))}
                  />
                ))}
              </div>
              <p className="mt-8 border-t border-border pt-5 text-xs leading-relaxed text-ink-muted">
                El resultado se recalcula al instante. Nada se guarda sobre ti: solo un
                contador anónimo de usos.
              </p>
            </div>
          </Reveal>

          <div className="space-y-6 lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-lg border border-border bg-card p-7 sm:p-9" style={{ borderTop: `3px solid ${calc.accent}` }}>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-muted">Resultado vital</p>
                <p
                  className="mt-4 break-words text-5xl font-bold leading-none tracking-tighter sm:text-6xl lg:text-7xl"
                  style={{ color: calc.accent }}
                  data-testid="calculator-result-big-number"
                >
                  <AnimatedNumber value={results.big.value} format={results.big.format} />
                </p>
                <p className="mt-3 font-serif text-xl italic text-muted-foreground">{results.big.label}</p>
                {count !== null && (
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted" data-testid="calculator-social-proof">
                    Este cálculo se ha realizado {nf(count)} veces en Curiosímetro
                  </p>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="result-breakdown">
                {results.rows.map((row) => (
                  <div key={row.label} className="rounded-md border border-border bg-secondary/30 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">{row.label}</p>
                    <p className="mt-2 font-mono text-2xl font-bold tracking-tight" data-testid="breakdown-value">
                      {row.format(row.value)}
                      {row.suffix && <span className="ml-1 text-sm font-normal text-ink-muted">{row.suffix}</span>}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <Chart chart={results.chart} accent={calc.accent} />
            </Reveal>

            <Reveal delay={0.24}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="result-equivalences">
                {results.equivalences.map((eq, i) => (
                  <div key={i} className="flex gap-4 rounded-md border border-border bg-card p-5">
                    <eq.icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color: calc.accent }} />
                    <div>
                      <p className="font-serif text-lg leading-snug">{eq.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{eq.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-16">
          <div className="border-t border-border pt-10" data-testid="article-section">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">En contexto</p>
            <div className="mt-6 max-w-3xl space-y-5">
              {ARTICLES[slug].map((p, i) => (
                <p
                  key={i}
                  className={
                    i === 0
                      ? "font-serif text-lg leading-relaxed text-foreground sm:text-xl"
                      : "text-base leading-relaxed text-muted-foreground"
                  }
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-16">
          <div className="border-t border-border pt-10" data-testid="method-section">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4" style={{ color: calc.accent }} />
              <h2 className="font-serif text-2xl tracking-tight">¿Cómo se calcula?</h2>
            </div>
            <div className="mt-8 grid gap-8 md:grid-cols-3">
              {[
                { icon: Scale, title: "La fórmula", body: results.method.formula },
                { icon: FlaskConical, title: "Las fuentes", body: results.method.sources },
                { icon: SlidersHorizontal, title: "Lo que puede variar", body: results.method.factors.join(" · ") },
              ].map((b, i) => (
                <div key={i}>
                  <b.icon className="h-4 w-4" style={{ color: calc.accent }} />
                  <h3 className="mt-3 font-mono text-xs uppercase tracking-[0.25em]">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.body}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-16">
          <div className="border-t border-border pt-10" data-testid="improve-section">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Mejora este número</p>
            <h2 className="mt-3 font-serif text-2xl tracking-tight sm:text-3xl">{calc.improveTitle}</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
              El dato está en tu mano: hábitos concretos que empujan esta cifra hacia el lado sano.
            </p>
            <div className="mt-8 grid gap-3 md:grid-cols-3">
              {calc.improve.map((tip, i) => (
                <div
                  key={i}
                  data-testid={`improve-tip-${i}`}
                  className="rounded-md border border-border bg-card p-6 transition-all hover:-translate-y-0.5 hover:shadow-offset"
                  style={{ borderTop: `3px solid ${calc.accent}` }}
                >
                  <tip.icon className="h-5 w-5" style={{ color: calc.accent }} />
                  <p className="mt-3 font-serif text-lg leading-snug">{tip.title}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{tip.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-16">
          <AdSlot slot="6824110255" label="Publicidad" minHeight={220} />
        </div>

        <Reveal className="mt-16">
          <h2 className="font-serif text-2xl tracking-tight">Sigue midiéndote</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="other-calculators">
            {others.map((c) => {
              const OIcon = ICONS[c.icon];
              return (
                <Link
                  key={c.slug}
                  to={`/${c.slug}`}
                  data-testid={`other-calculator-${c.slug}`}
                  className="group rounded-md border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-offset"
                >
                  <OIcon className="h-5 w-5" style={{ color: c.accent }} />
                  <p className="mt-3 font-serif text-lg">{c.title}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium" style={{ color: c.accent }}>
                    Calcular
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </Reveal>
      </main>
      <Footer />
    </>
  );
}

